import UriTemplate from 'uri-templates';
import axios from 'axios';
import sessionStorage from './utils/storage/session_storage';
import localStorage from './utils/storage/local_storage';
import { context } from './constants/context';
import dataCache from './data_cache';
import sharedHeader from './shared_header';
import {Collection} from "./collections/base";
import {HttpUtils} from "./utils/http";
import HttpTrackerService from './utils/http_tracker';

let sharedOptions = {};
let cancelToken;

/**
 * A list of functions that will be executed in the event of a 419 response from the server indicating that the auth token
 * session has expired.
 */
let sessionErrorHandler;

if (sessionStorage.getItem('no_server_cache')) {
    sharedHeader.set('no_server_cache', sessionStorage.getItem('no_server_cache'), sessionStorage);
}

function BaseResource(href, options, data, extra) {

    if (!options) options = {};
    if (!extra) extra = {};

    const links = {};
    const embedded = dataCache;

    href = getSelfLink(href, data).href;

    this.$href = (rel, params) => {
        if (!(rel in links)) return null;

        return hrefLink(links[rel], params);
    };
    this.$has = (rel) => {
        return rel in links;
    };
    this.$flush = (rel, params, all) => {
        const link = links[rel];
        return flushLink(link, params, all);
    };
    this.$get = (rel, params) => {
        const link = links[rel];
        if (link) {
            return callLink('GET', link, params);
        } else {
            return Promise.reject(`Rel '${rel}' not available`);
        }
    };
    this.$getCollection = (rel, params) => {
        let resource;
        return this.$get(rel, params)
            .then((res) => {
                resource = res;
                return resource.$unpack(rel);
            })
            .then((items) => {
                const collection = new Collection.Base(resource, items, params);
                collection.total_entries = resource.total_entries;
                return collection;
            });
    };    
    this.$unpack = (rel) => {
        // unpack the embedded data
        if (!data._embedded)
          return Promise.reject("no embedded data")
        if (!data._embedded[rel])
            rel = Object.keys(data._embedded)[0]
        if (!rel)
          return Promise.reject("no embedded data")
        const link = links[rel];
        return callLink('GET', link, {});
    };
    this.jsonify = () => {
        // return this base resource as a raw object that looks good as json
        return HttpUtils.jsonify(this)
    };
    this.halify = (json = null) => {
        // return the json object - but also with the embedded and links
        if (!json)
            json = this.jsonify();

        if (data._links) json._links = data._links;
        if (data._embedded) json._embedded = data._embedded;

        return json;
    };
    this.$post = (rel, params, data) => {
        const link = links[rel];
        if (link) {
            return callLink('POST', link, params, data);
        } else {
            return Promise.reject(`Rel '${rel}' not available`);
        }
    };
    this.$put = (rel, params, data) => {
        const link = links[rel];
        if (link) {
            return callLink('PUT', link, params, data);
        } else {
            return Promise.reject(`Rel '${rel}' not available`);
        }
    };
    this.$patch = (rel, params, data) => {
        const link = links[rel];
        return callLink('PATCH', link, params, data);
    };
    this.$del = (rel, params, data) => {
        const link = links[rel];
        if (link) {
            return callLink('DELETE', link, params, data);
        } else {
            return Promise.reject(`Rel '${rel}' not available`);
        }
    };
    this.$links = () => {
        return links;
    };
    this.$toStore = () => {
        return JSON.stringify({
            data: this,
            links,
            options: Object.assign({}, options, sharedOptions)
        });
    };
    this.setOption = (key, value) => {
        options[key] = value;
    };
    this.getOption = (key) => {
        return options[key];
    };
    this.$link = (rel) => {
        return links[rel];
    };

    Object.keys(data)
        .filter((key) => {
            return (key[0] != '_' && key[0] != '$');
        })
        .forEach(function (key) {
            this[key] = data[key];
        }, this)
    ;


    if (data._links) {
        Object
            .keys(data._links)
            .forEach((rel) => {
                let link = data._links[rel];
                link = normalizeLink(href, link);
                links[rel] = link;
            }, this)
        ;
    }

    if (data._embedded) {
        Object
            .keys(data._embedded)
            .forEach((rel) => {
                const embedded = data._embedded[rel];
                const link = getSelfLink(href, embedded);
                links[rel] = link;

                const resource = createResource(href, Object.assign({}, options, sharedOptions), embedded);

                embedResource(resource);

            }, this);
    }

    if (extra.is_new) this.is_new = true;

    function embedResource(resource) {
        if (Array.isArray(resource)) return resource.map((resource) => {
            return embedResource(resource);
        });

        const href = resource.$href('self');

        embedded.set(href, Promise.resolve(resource));
    }

    function hrefLink(link, params) {
        // don't mess with the original params, so create a local copy
        const localParams = Object.assign({}, params);

        params && Object.keys(params).forEach((paramKey) => {
            if (params[paramKey] && params[paramKey].constructor === Object && params[paramKey] instanceof Object) {
                const replacingString = [];

                // Search for nested params (hashes)
                Object.keys(params[paramKey]).forEach((hashKey) => {
                    replacingString.push(`${paramKey}[${hashKey}]`);
                    localParams[`${paramKey}[${hashKey}]`] = params[paramKey][hashKey];
                });

                if (Object.keys(params[paramKey]).length > 0) {
                    // replace the key with a string built to support nested params
                    // i.e filter_by[date] which was serialised as filter_by=date,<value>
                    const regex = new RegExp(`${paramKey}(?!\\[)`);
                    link.href = link.href.replace(regex, replacingString.join());
                }
            }
        });

        const href = link.templated
            ? new UriTemplate(link.href).fillFromObject(localParams || {})
            : link.href
        ;

        return href;
    }

    function callLink(method, link, params, data) {
        if (params == null) {
            params = {};
        }

        if (params.cancelToken) {
          cancelToken = params.cancelToken;
        }

        if (Array.isArray(link)) return Promise.all(link.map((link) => {
            if (method !== 'GET') throw 'method is not supported for arrays';

            return callLink(method, link, params, data);
        }));

        const linkHref = hrefLink(link, params);
        if (method === 'GET') {
            if (embedded.has(linkHref) && !params['no_cache']) {
                return embedded.get(linkHref);
            } else {
                return embedded.set(linkHref, callService(method, linkHref, Object.assign({}, options, sharedOptions), data));
            }
        } else {
            return callService(method, linkHref, Object.assign({}, options, sharedOptions), data);
        }

    }

    function flushLink(link, params, all) {
        if (Array.isArray(link)) {
            return link.map(linkElement => flushLink(linkElement, params, all));
        } else {
            const linkHref = hrefLink(link, params);
            if (all) {
                embedded.delMatching(linkHref);
            } else if (embedded.has(linkHref)) {
                embedded.del(linkHref);
            }
        }
    }

}

function createResource(href, options, data, extra) {
    if (Array.isArray(data)) return data.map((data) => {
        return createResource(href, options, data, extra);
    });

    return new BaseResource(href, options, data, extra);
}

function normalizeLink(baseHref, link) {
    if (Array.isArray(link)) return link.map((link) => {
        return normalizeLink(baseHref, link);
    });

    if (link) {
        if (typeof link === 'string') link = {href: link};
        link.href = resolveUrl(baseHref, link.href);
    } else {
        link = {href: baseHref};
    }

    return link;
}

function getSelfLink(baseHref, resource) {
    if (Array.isArray(resource)) return resource.map((resource) => {
        return getSelfLink(baseHref, resource);
    });

    return normalizeLink(baseHref, resource && resource._links && resource._links.self);
}

function callService(method, href, options = {}, data) {

    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/hal+json,application/json'
    };

    const storage = context.storage === 'local' ? localStorage : sessionStorage;

    if (options.bypass_auth) headers['Bypass-Auth'] = options.bypass_auth;

    if (context.appId) headers['App-Id'] = context.appId;
    if (context.appKey) headers['App-Key'] = context.appKey;
    if (context.authorization) headers['Authorization'] = context.authorization; // required by dev-portal

    if (storage.getItem('auth_token')) headers['Auth-Token'] = storage.getItem('auth_token');

    if (sharedHeader.has('no_server_cache')) headers['No-Server-Cache'] = sharedHeader.get('no_server_cache');


    // Once the API support for multi language is merged, we can remove the context field
    if (context.enableMultiLanguageSupport && localStorage.NG_TRANSLATE_LANG_KEY) {
        headers['Accept-Language'] = localStorage.NG_TRANSLATE_LANG_KEY;
    }

    const {withCredentials} = context;

    const stack = new Error().stack;

    const resource = (
        axios({
            url: options.transformUrl ? options.transformUrl(href) : href,
            method,
            headers,
            data,
            withCredentials,
            cancelToken
        })
        .then((res) => {


            HttpTrackerService.success({
                method,
                url: options.transformUrl ? options.transformUrl(href) : href,
                headers,
                data,
                stack,
                result: res
            });            

            let token;

            // copy out the auth token from the header if the response is new resource
            // Note: we only take the auth token from created responses as at the app layer, success responses might be cached results, thus we don't want to use the auth token from these
            if (res.status === 201) {
                token = res.data && res.data['auth_token'] || res.headers && res.headers['auth-token'];
            }

            if (token) {
              storage.setItem('auth_token', token);
            }

            switch (res.status) {
                case 200:
                    //For back-ends that return `"null"` instead of `null` -_-
                    if (res.data && res.data !== '"null"') return createResource(href, options, res.data);
                    return null;

                case 201:
                    if (res.data) return createResource(href, options, res.data, {is_new: true});
                    if (res.headers['Content-Location']) return res.headers['Content-Location'];
                    return null;

                case 204:
                    return null;

                default:
                    return Promise.reject(res);
            }
        })
        .catch((error) => {

            HttpTrackerService.error({
                method,
                url: options.transformUrl ? options.transformUrl(href) : href,
                headers,
                data,
                stack,
                result: error
            });

            if (error && error.response) {
                if (error.response.status === 419) {
                    sessionErrorHandler('expired');
                    return Promise.reject(error.response.data);
                }

                if (error.response.config.url.includes('login')) {
                    // If a company isn't found - API incorrectly returns a 403 with a not found message
                    if (error.response.status === 403 && error.response.data.custom_details && !error.response.data.custom_details.message.includes('Couldn\'t find Company')) {
                        sessionErrorHandler('forbidden');
                        return Promise.reject(error.response.data);
                    }

                    if (error.response.status === 400 && error.response.data.error && error.response.data.error.includes('Requires a valid Auth-Token')) {
                        sessionErrorHandler('missing-auth-token');
                        return Promise.reject(error.response.data);
                    }

                    if (
                        error.response.status === 404
                        && error.response.data
                        && error.response.data.detail
                        && error.response.data.detail.match(/couldn't find login without an id/i)
                    ) {
                        sessionErrorHandler('expired');
                        return Promise.reject(error.response.data);
                    }
                }
            }

            return Promise.reject(error);
        })

    );

    return resource;
}

function parseHal(data) {
    return createResource(data._links.self.href, null, data);
}

function resolveUrl(baseHref, href) {
    let resultHref = '';
    const reFullUrl = /^((?:\w+\:)?)((?:\/\/)?)([^\/]*)((?:\/.*)?)$/;
    const baseHrefMatch = reFullUrl.exec(baseHref);
    const hrefMatch = reFullUrl.exec(href);

    for (let partIndex = 1; partIndex < 5; partIndex++) {
        if (hrefMatch[partIndex]) resultHref += hrefMatch[partIndex];
        else resultHref += baseHrefMatch[partIndex];
    }

    return resultHref;
}

const halClient = {
    addSessionErrorHandler(handler) {
        sessionErrorHandler = handler;
    },
    getSharedOptions: () => {
        return sharedOptions;
    },
    clearCache: (str) => {
        dataCache.delMatching(str);
    },
    flushCache: () => {
        dataCache.reset();
    },
    reset: () => {
        dataCache.reset();
        sharedHeader.reset();
        sharedOptions = {};
        sessionStorage.clear();
    },
    createResource: (store) => {
        if (typeof store === 'string') {
            store = JSON.parse(store);
        }
        const resource = store.data;
        resource._links = store.links;
        const key = store.links.self.href;
        const options = store.options;
        return new BaseResource(key, options, resource);
    },
    $get: (href, options) => {
        if (
            dataCache.has(href) &&
            (!options || !options.no_cache)
        ) {
            return dataCache.get(href);
        }
        return dataCache.set(href, callService('GET', href, options));
    },
    $post: (href, options, data) => {
        return callService('POST', href, Object.assign({}, options, sharedOptions), data);
    },
    $put: (href, options, data) => {
        return callService('PUT', href, Object.assign({}, options, sharedOptions), data);
    },
    $patch: (href, options, data) => {
        return callService('PATCH', href, Object.assign({}, options, sharedOptions), data);
    },
    $del: (href, options, data) => {
        return callService('DELETE', href, Object.assign({}, options, sharedOptions), data);
    },
    $parse: (data) => {
        return parseHal(data);
    },
    _http: axios
};

export {
    halClient, sharedHeader, dataCache, BaseResource
};
